import type React from 'react';
import { useMemo, useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from '@/components/card/card';
import { KeenIcon, SubscriptionPlansCard } from '@/components';
import EntityCancelPlanButton from '@/components/subscription-management/EntityCancelPlanButton';
import { SubscriptionsService } from '@/services/subscriptions/subscriptionsService';
import { ISubscription } from '@/types/subscription-management.types';
import { useSelector } from 'react-redux';
import { RootState } from '@/store/store';
import { Briefcase } from 'lucide-react';
import { DMORegisteredProfile } from '@/types/dmo-management.types';
import { usePermissions } from '@/hooks/usePermissions';
import { formatDisplayDate } from '@/utils/Date';
// import { LegalAcceptanceCard } from '@/components/legal-acceptance/LegalAcceptanceCard';

interface DMOUserDetailsViewProps {
  user: DMORegisteredProfile;
}

export const DMOUserDetailsView: React.FC<DMOUserDetailsViewProps> = ({
  user,
}) => {
  const navigate = useNavigate();
  const { hasPermission } = usePermissions();
  const userData = useSelector((state: RootState) => state.auth.userData);
  const id = user?.id;

  const canReadSubscriptions = hasPermission('subscription_management_read');
  const isLoggedInUserDmoAdmin =
    !!userData?.id && !!user?.user_id && userData.id === user.user_id;
  const canAccessSubscriptions = canReadSubscriptions || isLoggedInUserDmoAdmin;

  // Subscription plans state
  const [subscriptions, setSubscriptions] = useState<ISubscription[]>([]);
  const [subscriptionsLoading, setSubscriptionsLoading] = useState(false);
  const [currentPlanId, setCurrentPlanId] = useState<string | null>(null);
  const [associatedClubs, setAssociatedClubs] = useState<string[]>([]);
  const [subscriptionMessage, setSubscriptionMessage] = useState('');
  const [planCancellationRequested, setPlanCancellationRequested] =
    useState(false);
  const [planSubscriptionEndDate, setPlanSubscriptionEndDate] = useState<
    string | null
  >(null);
  const [subscriptionRefreshKey, setSubscriptionRefreshKey] = useState(0);

  // Fetch DMO subscription plans and current plan
  useEffect(() => {
    const fetchSubscriptions = async () => {
      try {
        setSubscriptionsLoading(true);

        const response =
          await SubscriptionsService.getSubscriptionsByType('dmo');
        setSubscriptions(response.subscriptions || []);

        if (id) {
          const currentPlan = await SubscriptionsService.getCurrentDMOPlan(id);
          setCurrentPlanId(currentPlan?.subscription?.id || null);
          setPlanCancellationRequested(
            !!currentPlan?.cancellation_requested_at
          );
          setPlanSubscriptionEndDate(
            currentPlan?.subscription_end_date ?? null
          );

          if (
            currentPlan?.associated_clubs &&
            currentPlan.associated_clubs.length > 0
          ) {
            setAssociatedClubs(currentPlan.associated_clubs);
            setSubscriptionMessage(currentPlan.message || '');
          } else {
            setAssociatedClubs([]);
            setSubscriptionMessage('');
          }
        }
      } catch (error) {
        console.error('Failed to fetch subscriptions:', error);
        setSubscriptions([]);
        setCurrentPlanId(null);
        setAssociatedClubs([]);
        setSubscriptionMessage('');
      } finally {
        setSubscriptionsLoading(false);
      }
    };

    if (id && canAccessSubscriptions) {
      fetchSubscriptions();
    } else if (!canAccessSubscriptions) {
      setSubscriptions([]);
      setSubscriptionsLoading(false);
      setCurrentPlanId(null);
      setAssociatedClubs([]);
      setSubscriptionMessage('');
    }
  }, [id, canAccessSubscriptions, subscriptionRefreshKey]);

  const handleUpgrade = (planId: string) => {
    const selectedPlan = subscriptions.find((s) => s.id === planId);
    if (!selectedPlan || !id) return;

    navigate('/subscription/payment', {
      state: {
        subscriptionId: planId,
        subscriptionName: selectedPlan.name,
        subscriptionPrice: selectedPlan.price,
        entityType: 'dmo',
        entityId: id,
        returnUrl: `/dmo-management/details/${id}`,
      },
    });
  };

  const currentSubscription =
    subscriptions.find((s) => s.id === currentPlanId) || null;

  const linkedRegionsList = useMemo(() => {
    // If we have detailed objects, extract the names
    if (user?.linked_region_details && user.linked_region_details.length > 0) {
      return user.linked_region_details.map((r) => r.name);
    }

    // Fallback to simple linked_regions list
    if (!user?.linked_regions) return [] as string[];
    if (Array.isArray(user.linked_regions)) return user.linked_regions;
    return user.linked_regions
      .split(/[\n,]/)
      .map((r: string) => r.trim())
      .filter(Boolean);
  }, [user?.linked_regions, user?.linked_region_details]);

  if (!hasPermission('dmo_registration_management_read')) {
    return (
      <div className="bg-gray-100 p-6 rounded-lg shadow-lg flex flex-col">
        <div className="text-center py-4 text-gray-500" role="alert">
          You don't have permission to view DMO user details.
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
        {/* Left column */}
        <div className="space-y-6 lg:col-span-2">
          <Card>
            <CardHeader>
              <CardTitle>Organisation</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4 text-sm text-gray-800">
                <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-2">
                  <div>
                    <p className="font-semibold text-base">
                      {user?.organisation_name || '-'}
                    </p>
                    <p className="text-xs text-gray-500 mt-0.5">
                      {user?.organisation_type ||
                        'Organisation type not specified'}
                    </p>
                  </div>
                  {user?.public_homepage && (
                    <a
                      href={user.public_homepage}
                      target="_blank"
                      rel="noreferrer"
                      className="inline-flex items-center gap-1 text-xs font-medium text-blue-600 contrast:text-blue-200 hover:text-blue-800"
                    >
                      <KeenIcon
                        type="filled"
                        icon="link"
                        className="text-blue-500 text-sm"
                      />
                      {user.public_homepage}
                    </a>
                  )}
                </div>
                <div>
                  <h3 className="font-semibold mb-1 text-sm">Description</h3>
                  <p className="text-sm text-gray-700 leading-relaxed break-all">
                    {user?.description || 'No description provided.'}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Linked Regions</CardTitle>
            </CardHeader>
            <CardContent>
              {linkedRegionsList.length > 0 ? (
                <div className="flex flex-wrap gap-2">
                  {linkedRegionsList.map((regionName, index) => (
                    <span
                      key={index}
                      className="inline-flex items-center rounded-full border border-gray-300 bg-gray-100 px-2.5 py-0.5 text-xs font-medium text-gray-800"
                    >
                      {regionName}
                    </span>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-gray-600">No regions linked.</p>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Right column */}
        <div className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Contact Information</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3 text-sm text-gray-800">
                <div className="flex items-center gap-1">
                  <div className="w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center">
                    <KeenIcon
                      type="filled"
                      icon="user"
                      className="text-gray-500"
                    />
                  </div>
                  <div className="flex flex-col">
                    <span className="text-xs text-gray-500">
                      Contact Person
                    </span>
                    <span className="font-medium">{user?.name || '-'}</span>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center">
                    <KeenIcon
                      type="filled"
                      icon="sms"
                      className="text-gray-500"
                    />
                  </div>
                  <div className="flex flex-col">
                    <span className="text-xs text-gray-500">Contact Email</span>
                    <span className="font-medium break-all">
                      {user?.contact_email || '-'}
                    </span>
                  </div>
                </div>
                {user?.email && user?.email !== user?.contact_email && (
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center">
                      <KeenIcon
                        type="filled"
                        icon="sms"
                        className="text-gray-500"
                      />
                    </div>
                    <div className="flex flex-col">
                      <span className="text-xs text-gray-500">
                        System Email
                      </span>
                      <span className="font-medium">{user.email}</span>
                    </div>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>

          {/* <LegalAcceptanceCard
            summary={user.legal_acceptance_summary}
            className=""
          /> */}

          <Card>
            <CardHeader>
              <CardTitle>Account Details</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4 text-sm text-gray-800">
                <div className="flex items-center justify-between">
                  <span className="text-gray-600">Joined Date</span>
                  <span className="font-medium">
                    {user?.created_at
                      ? formatDisplayDate(new Date(user.created_at))
                      : '-'}
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-gray-600">Language</span>
                  <span className="font-medium uppercase">
                    {user?.language || 'en'}
                  </span>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Subscription Plans Section */}
      <div className="mt-8">
        {canAccessSubscriptions && !isLoggedInUserDmoAdmin && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Briefcase className="h-5 w-5 text-primary" /> DMO Subscription
                Plans
              </CardTitle>
            </CardHeader>
            <CardContent className="px-6 pb-6">
              {id && associatedClubs.length === 0 && (
                <EntityCancelPlanButton
                  entityType="dmo"
                  entityId={id}
                  entityName={user?.name}
                  planPrice={currentSubscription?.price}
                  cancellationRequested={planCancellationRequested}
                  subscriptionEndDate={planSubscriptionEndDate}
                  onSuccess={() =>
                    setSubscriptionRefreshKey((key) => key + 1)
                  }
                  className="mb-4"
                />
              )}
              {subscriptionsLoading ? (
                <SubscriptionPlansCard
                  subscriptions={
                    currentSubscription ? [currentSubscription] : []
                  }
                  loading={subscriptionsLoading}
                  currentPlanId={currentPlanId}
                />
              ) : associatedClubs.length > 0 ? (
                <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
                  <p className="text-sm text-blue-900 font-medium mb-2">
                    {subscriptionMessage ||
                      'This DMO’s current subscription is associated with the following club(s)'}
                  </p>
                  <ul className="list-disc list-inside text-sm text-blue-800">
                    {associatedClubs.map((club, index) => (
                      <li key={index}>{club}</li>
                    ))}
                  </ul>
                </div>
              ) : currentSubscription ? (
                <SubscriptionPlansCard
                  subscriptions={[currentSubscription]}
                  loading={false}
                  currentPlanId={currentPlanId}
                />
              ) : (
                <p className="text-sm text-muted-foreground">
                  No current plan found for this DMO.
                </p>
              )}
            </CardContent>
          </Card>
        )}
        {canAccessSubscriptions && isLoggedInUserDmoAdmin && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Briefcase className="h-5 w-5 text-primary" /> DMO Subscription
                Plans
              </CardTitle>
            </CardHeader>
            <CardContent className="px-6 pb-6">
              {associatedClubs.length > 0 && (
                <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                  <p className="text-sm text-blue-900 font-medium mb-2">
                    {subscriptionMessage ||
                      'This DMO’s current subscription is associated with the following club(s)'}
                  </p>
                  <ul className="list-disc list-inside text-sm text-blue-800">
                    {associatedClubs.map((club, index) => (
                      <li key={index}>{club}</li>
                    ))}
                  </ul>
                </div>
              )}
              <SubscriptionPlansCard
                subscriptions={subscriptions}
                loading={subscriptionsLoading}
                currentPlanId={currentPlanId}
                onUpgrade={handleUpgrade}
              />
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
};
